import React, { createContext, useContext, useState, useEffect } from 'react';
import { menuService } from '../admin/services/adminAuth';
import { useAuth } from '../../auth/hooks/auth';

export interface ApiMenu {
  id: number;
  displayName: string;
  route: string;
  group: string;
}

interface MenuContextType {
  adminMenus: ApiMenu[];
  menusLoading: boolean;
  refreshMenus: () => Promise<void>;
}

const MenuContext = createContext<MenuContextType | null>(null);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminMenus, setAdminMenus] = useState<ApiMenu[]>([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();

  const loadMenus = async () => {
    if (user?.role === 'author' || !isAuthenticated) {
      setAdminMenus([]);
      return;
    }
    setMenusLoading(true);
    try {
      const res = await menuService.getSideMenuByRole();
      if (res.isSuccess) {
        setAdminMenus(res.data);
        localStorage.setItem('menus', JSON.stringify(res.data));
      }
    } catch (error) {
      console.error('Failed to load menus in MenuProvider:', error);
    } finally {
      setMenusLoading(false);
    }
  };

  useEffect(() => {
    loadMenus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roleId, isAuthenticated]);

  return (
    <MenuContext.Provider value={{ adminMenus, menusLoading, refreshMenus: loadMenus }}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenuContext = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenuContext must be used within a MenuProvider');
  }
  return context;
};
